import React, { useMemo } from 'react';
import styled from 'styled-components';
import { AlertHeader, Button, H3, ModalFactory, ModalFooter, ModalLayout, ModalText, Text } from 'components';
import { theme, toRem } from 'design';
import { ICheck, TicketOverrideType } from 'types';

const ModalContent = styled(ModalLayout)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: ${toRem(265)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    padding: ${toRem(28)};
  }
`;

const DetailsWrapper = styled.section`
  display: flex;
  padding: ${toRem(16)};
  flex-direction: column;
  justify-content: center;
  gap: ${toRem(8)};
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[50]};
  background: ${theme.color.neutral[50]};
  width: 100%;
  margin-top: ${toRem(16)};
  align-items: center;

  h3 {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
  }

  p {
    font-size: ${toRem(12)};
    color: ${theme.color.neutral[500]};
    text-align: center;
  }
`;

interface IUndoCheckChangesModalProps {
  checks: ICheck[];
  isLoading?: boolean;
  onClose: () => void;
  onApply: () => void;
}

export const UndoCheckChangesModal = ({ checks, onClose, onApply, isLoading }: IUndoCheckChangesModalProps) => {
  const splitChecks = useMemo(() => checks.filter((check) => check.override?.type === TicketOverrideType.SPLIT), [checks]);
  const ignoredChecks = useMemo(() => checks.filter((check) => check.override?.type === TicketOverrideType.IGNORE), [checks]);

  return (
    <ModalFactory customModal onClose={onClose} isActive>
      <ModalContent onClose={onClose} isActive data-cy="undo-check-changes-modal">
        <AlertHeader title="Undo changes?" />
        <ModalText className="modal-text">This will undo the following action{splitChecks.length + ignoredChecks.length > 1 ? 's' : ''}</ModalText>
        {splitChecks.length > 0 && (
          <DetailsWrapper data-cy="split-check-details">
            <H3>Split Check{splitChecks.length > 1 ? 's' : ''}</H3>
            <Text>{splitChecks.map((check) => `#${check.displayId}`).join(', ')}</Text>
          </DetailsWrapper>
        )}
        {ignoredChecks.length > 0 && (
          <DetailsWrapper data-cy="ignored-check-details">
            <H3>Ignored Check{ignoredChecks.length > 1 ? 's' : ''}</H3>
            <Text>{ignoredChecks.map((check) => `#${check.displayId}`).join(', ')}</Text>
          </DetailsWrapper>
        )}
        <ModalFooter columns={2} data-cy="modal-footer">
          <Button data-cy="close-btn" label="Cancel" type="button" appearance="white" size="large" onClick={onClose} disabled={isLoading} />
          <Button data-cy="apply-btn" label="Undo Changes" type="button" appearance="destructive" size="large" onClick={onApply} isProcessing={isLoading} />
        </ModalFooter>
      </ModalContent>
    </ModalFactory>
  );
};
